import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const ITEMS = [
  {
    q: "What is Wet Cabbage?",
    a: "A quietly confident streetwear universe built around modern leisure behaviour. Premium hoodies, crewnecks, caps and accessories inspired by airport chaos, pub culture, BBQ disasters, retail parks, suburban survival and the strange beauty of ordinary life.",
  },
  {
    q: "What collections do you make?",
    a: "Six divisions inside the Wet Cabbage universe \u2014 Travel (aggressively relaxed vacation behaviour), Everyday Life (Aldi expeditions, meal deals, retail parks), Pub (beer garden survival gear, quiz-night confidence), Sports & Leisure (performancewear for casual inactivity), Dad Life (BBQ disasters, lawn mowing pride, DIY confidence) and Home (flat-packed furniture chaos, leftover screws, domestic survival). Each division is a quiet observation on modern leisure.",
  },
  {
    q: "How long does shipping take?",
    a: "Everything is made to order, so most items ship within 3\u20136 business days after we make them. We don\u2019t keep giant warehouses full of lonely hoodies wondering where it all went wrong.",
  },
  {
    q: "Do you ship internationally?",
    a: "Yes. We ship globally through our print-on-demand partners. Live international rates and delivery windows are shown at checkout.",
  },
  {
    q: "What is your returns policy?",
    a: "Unworn, unwashed items can be returned within 14 days of delivery. Print-on-demand goods made to your size aren\u2019t restocked, so please check our size guide before ordering. Sizing issue? Email us with a photo of the tag and we\u2019ll sort it.",
  },
  {
    q: "How do your sizes run?",
    a: "Heavyweight, slightly relaxed, true-to-size in most cases. If you usually live somewhere between sizes, go up. We\u2019d rather you swim slightly than feel cling-filmed.",
  },
  {
    q: "Care instructions?",
    a: "Cold wash, inside out, hang to dry. Avoid tumble dryers if you have feelings about your garments. Iron on the reverse if you must iron at all. Do not boil. Do not use as a tea towel.",
  },
  {
    q: "I want sponsorship for my five-a-side / pub quiz / BBQ team. How do I pitch?",
    a: "We sponsor underdogs, not superstars. Sunday league teams that lose 8\u20111 but celebrate one goal like a World Cup final. BBQ teams cooking exclusively on confidence. Pub quiz groups with an inspiring relationship with 9th place. Pitch your underdog through the form on our Sponsorship page \u2014 honest answer wins over impressive answer.",
  },
  {
    q: "Do you do wholesale or collaborations?",
    a: "Occasionally, when it\u2019s the right kind of weird. Email moist@wetcabbage.com with the subject line \u201cI have a strange idea\u201d and a one-paragraph description of what you\u2019re thinking.",
  },
  {
    q: "Do you use AI-generated artwork?",
    a: "No. We prefer hand-drawn, scanned, photographed and lovingly mishandled work. The whole point is that things look made by humans and supervised by a dog.",
  },
  {
    q: "I have a question that isn\u2019t answered here.",
    a: "Email moist@wetcabbage.com. We answer most things within a few days. We answer all things eventually. Mush insists on reviewing the long ones.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <div data-testid="faq-page" className="bg-[#F9F9F4] text-[#1A3626]">
      <SEO
        title="FAQ"
        path="/faq"
        description="Wet Cabbage FAQ. Sizing, shipping, returns, sponsorship and other questions about premium clothing for beautifully unserious people."
      />

      {/* MASTHEAD */}
      <section className="bg-[#F9F9F4]" data-testid="faq-masthead">
        <div className="mx-auto max-w-[1100px] px-6 pb-10 pt-20 md:px-10 md:pb-14 md:pt-28">
          <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#1A3626]/65 md:text-xs">
            Frequently asked
          </div>
          <h1
            className="font-display mt-5 text-5xl font-black uppercase leading-[0.9] tracking-tight sm:text-6xl md:text-7xl lg:text-8xl"
            data-testid="faq-headline"
          >
            FAQ.
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#1A3626] md:text-xl md:leading-[1.6]">
            Sizing, shipping, returns, sponsorship and the meaning of Certified
            Damp. If we&rsquo;ve missed something, write to{" "}
            <a
              href="mailto:moist@wetcabbage.com"
              className="underline decoration-[#93C065] decoration-2 underline-offset-4 hover:text-[#1A3626]/80"
            >
              moist@wetcabbage.com
            </a>{" "}
            and we&rsquo;ll either answer or invent something.
          </p>
        </div>
      </section>

      {/* ACCORDION */}
      <section className="bg-[#F9F9F4] pb-16 md:pb-24" data-testid="faq-list">
        <div className="mx-auto max-w-[900px] px-6 md:px-10">
          <div className="border-y-2 border-[#1A3626]">
            {ITEMS.map((it, i) => {
              const isOpen = open === i;
              return (
                <div
                  key={i}
                  className={i === 0 ? "" : "border-t border-[#1A3626]/15"}
                  data-testid={`faq-item-${i}`}
                >
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className={`flex w-full items-center justify-between gap-6 px-2 py-6 text-left transition-colors md:px-3 md:py-7 ${
                      isOpen ? "bg-transparent" : "bg-transparent hover:bg-[#EBE7DD]/50"
                    }`}
                    data-testid={`faq-toggle-${i}`}
                    aria-expanded={isOpen}
                  >
                    <span className="font-display text-base font-black uppercase leading-tight tracking-tight text-[#1A3626] md:text-xl">
                      {it.q}
                    </span>
                    {isOpen ? (
                      <Minus className="h-5 w-5 shrink-0 text-[#93C065]" strokeWidth={3} />
                    ) : (
                      <Plus className="h-5 w-5 shrink-0 text-[#1A3626]" strokeWidth={3} />
                    )}
                  </button>
                  {isOpen && (
                    <div
                      className="px-2 pb-7 text-base leading-relaxed text-[#1A3626]/90 md:px-3 md:text-lg md:leading-[1.7]"
                      data-testid={`faq-answer-${i}`}
                    >
                      {it.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F9F9F4] pb-24 pt-4 md:pb-32" data-testid="faq-cta">
        <div className="mx-auto max-w-[1100px] px-6 text-center md:px-10">
          <p className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#1A3626]/65 md:text-xs">
            Stop reading. Start shopping.
          </p>
          <h2 className="font-display mt-5 text-3xl font-black uppercase leading-[0.95] tracking-tight sm:text-4xl md:text-5xl">
            Buy a hoodie.<br />Join the universe.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/shop"
              className="font-display inline-flex items-center gap-3 border-2 border-[#1A3626] bg-[#1A3626] px-7 py-4 text-xs font-black uppercase tracking-[0.32em] text-[#F9F9F4] transition-all hover:-translate-y-0.5 hover:bg-transparent hover:text-[#1A3626] md:px-9 md:py-5 md:text-sm md:tracking-[0.36em]"
              data-testid="faq-shop-cta"
            >
              Shop the drop
            </Link>
            <Link
              to="/sponsorship"
              className="font-display inline-flex items-center gap-3 border-2 border-[#1A3626] px-7 py-4 text-xs font-black uppercase tracking-[0.32em] text-[#1A3626] transition-all hover:-translate-y-0.5 hover:bg-[#1A3626] hover:text-[#F9F9F4] md:px-9 md:py-5 md:text-sm md:tracking-[0.36em]"
              data-testid="faq-sponsorship-cta"
            >
              Pitch your underdog
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
